// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import { RenderMode } from './render-mode';
import styles from './RenderModeToggle.module.css';

interface RenderModeToggleProps {
  /** Currently active render mode. */
  mode: RenderMode;
  /** Called with the new mode when the user clicks the other option. */
  onChange: (mode: RenderMode) => void;
}

/**
 * Two-state segmented control that switches between SFD (stock-and-flow)
 * and CLD (causal-loop) rendering for the active model.
 *
 * Implements the UI surface required by D2 acceptance criterion 14:
 * *"A new CLD rendering mode is available alongside the existing SFD
 * mode, toggled via a UI control in the Editor."*
 *
 * The control owns no state of its own -- mode is a prop and changes
 * flow back to the Editor through `onChange`.  This keeps the toggle
 * itself a pure presentation component and lets Editor be the single
 * source of truth for the active mode.
 */
export class RenderModeToggle extends React.PureComponent<RenderModeToggleProps> {
  handleSfdClick = (): void => {
    if (this.props.mode !== 'sfd') {
      this.props.onChange('sfd');
    }
  };

  handleCldClick = (): void => {
    if (this.props.mode !== 'cld') {
      this.props.onChange('cld');
    }
  };

  render(): React.ReactNode {
    const { mode } = this.props;
    return (
      <div className={styles.toggle} role="group" aria-label="Diagram render mode">
        <button
          type="button"
          className={styles.option}
          aria-pressed={mode === 'sfd'}
          onClick={this.handleSfdClick}
        >
          SFD
        </button>
        <button
          type="button"
          className={styles.option}
          aria-pressed={mode === 'cld'}
          onClick={this.handleCldClick}
        >
          CLD
        </button>
      </div>
    );
  }
}
